import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {map, Observable, of, shareReplay, switchMap, tap} from 'rxjs';
import {Tpa} from './tpa';
import {EnrolmentBlobStorageLocation} from '../../../prudent-plus-shared/services/blob-storage/enrolment-blob-storage';
import {EnrolmentBlobStorageService} from '../../../prudent-plus-shared/services/blob-storage/enrolment-blob-storage.service';
import {ENVIRONMENT} from 'src/environments/environment.token';

@Injectable({
  providedIn: 'root',
})
export class TpaService {
  private http = inject(HttpClient);
  private blobStorageService = inject(EnrolmentBlobStorageService);
  private environment = inject(ENVIRONMENT);

  private tpas: Tpa[] = [];
  private tpaById = new Map<number, Tpa>();

  tpa(id: number): Tpa {
    return this.tpaById.get(id)!;
  }

  getTpa(): Tpa[] {
    return this.tpas;
  }

  loadTpa(includeInactive: boolean, reload: boolean = false): Observable<boolean> {
    if (this.tpas.length > 0 && !reload) return of(true);

    return this._loadTpa(includeInactive);
  }

  private _loadTpa(includeInactive: boolean): Observable<boolean> {
    let includeInactiveValue = includeInactive ? 1 : 0;

    return this.http.get<Tpa[]>(`${this.environment.appConfig.apiBaseUrl}/tpa`, {
      params: {
        include_inactive: includeInactiveValue,
      },
    }).pipe(map((response) => {
      this.tpas = response.sort((tpa1, tpa2) => tpa1.name.localeCompare(tpa2.name));
      this.tpaById.clear();
      this.tpas.forEach((tpa) => this.tpaById.set(tpa.id, tpa));

      return true;
    }));
  }

  addTpa(tpa: Tpa) {
    return this.http.post(`${this.environment.appConfig.apiBaseUrl}/tpa`, tpa).pipe(switchMap(() => {
      return this._loadTpa(true);
    }));
  }

  _integrations$ = this.http.get<string[]>(`${this.environment.appConfig.apiBaseUrl}/integration-keys`).pipe(shareReplay());

  get integrations(): Observable<string[]> {
    return this._integrations$;
  }

  updateTpa(
    tpa: Tpa,
    tpaLogoFile?: File
  ): Observable<any> {
    const update = () => {
      return this.http.put<Tpa>(`${this.environment.appConfig.apiBaseUrl}/tpa`, tpa).pipe(
        tap((response: any) => {
          this.tpas = this.tpas.map((tpa) => (tpa.id === response.id ? response : tpa));
          this.tpaById.set(response.id, response);
        })
      );
    };

    if (tpaLogoFile) {
      return this.blobStorageService.uploadBlob(tpa.id,
        tpaLogoFile,
        tpaLogoFile.name,
        EnrolmentBlobStorageLocation.TPA_LOGO)
        .pipe(
          switchMap((fileUrl: string) => {
            tpa.logo = fileUrl;

            return update();
          })
        );
    } else {
      return update();
    }
  }

  reloadNetworkHospitals(key: string) {
    return this.http.get(`${this.environment.appConfig.apiBaseUrl}/populate-network-hospitals`, {
      params: {
        key: key,
      },
    });
  }
}
